import { PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { countLabel } from '@/utils/plural'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import {
	useCreateIdea,
	useIdeas,
	useTrip,
	useUploadFiles,
} from '@hooks'
import { getErrorMessage } from '@lib/apiError'
import { EventCategory, IdeaCreateSource } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import AddIdeaModal from './AddIdeaModal'
import { filterIdeas } from './ideaFilters'
import { IdeaDraft, toCreatePayload } from './ideaForm'
import { IdeaFilterRow, IdeaTileGrid } from './IdeaGrid'
import {
	AddIdeaButton,
	NoMatches,
} from './IdeasPage.styles'
import { Idea } from './types'
import { ErrorNote } from '@components/Notice'
import { TestIds } from '@/testIds'

/**
 * Member ideas at `/trips/:tripId/ideas` — one flat grid of square tiles
 * (link-preview thumbnail over the name), the same tile language as the public
 * ideas page and the iOS collection, narrowed by the shared category/location
 * dropdowns. Replaces the old per-category kanban board: category is now a
 * filter, not a column. Clicking a tile opens the idea's page, as on iOS, where
 * editing, deleting and its Discussion live.
 */
const IdeasPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: ideasData, isLoading: ideasLoading, error: ideasError } = useIdeas(tripId)
	const navigate = useNavigate()

	const createIdeaMutation = useCreateIdea()
	const uploadFilesMutation = useUploadFiles()

	/** Which button opened the add modal, sent with the create; null while it's closed. */
	const [addingFrom, setAddingFrom] = useState<IdeaCreateSource | null>(null)
	const [selectedCategory, setSelectedCategory] = useState<EventCategory | null>(null)
	const [selectedLocation, setSelectedLocation] = useState<string | null>(null)

	const handleCreateIdea = async (draft: IdeaDraft, pendingFiles: File[]) => {
		if (!tripId) return
		try {
			const createdIdea = await createIdeaMutation.mutateAsync({
				tripId,
				ideaData: { ...toCreatePayload(draft, parseInt(tripId)), ...(addingFrom && { source: addingFrom }) },
			})
			if (pendingFiles.length > 0) {
				await uploadFilesMutation.mutateAsync({
					tripId,
					files: pendingFiles,
					resourceType: 'IDEA',
					resourceId: createdIdea.id,
				})
			}
			toast.success('Idea added successfully!')
		} catch (error) {
			console.error('Failed to create idea:', error)
			toast.error(getErrorMessage(error, 'Failed to add idea. Please try again.'))
		}
	}

	if (tripLoading || ideasLoading) {
		return (
			<PageLoader label="Loading ideas…" />
		)
	}

	if (tripError || ideasError) {
		return (
			<ErrorNote>Error loading ideas. Please try again.</ErrorNote>
		)
	}

	if (!trip) {
		return (
			<ErrorNote>Trip not found</ErrorNote>
		)
	}

	const ideas: Idea[] = ideasData?.ideas || []
	const visibleIdeas = filterIdeas(ideas, {
		category: selectedCategory,
		location: selectedLocation,
	})

	return (
		<PageScroll>
			{ideas.length === 0 ? (
				<EmptyState
					art="ideas"
					title="No ideas yet"
					description="Save ideas from Google Maps, TikTok, Instagram, and more, or add them manually."
				>
					<EmptyStateButton onClick={() => setAddingFrom('empty_state')} data-testid={TestIds.common.fab}>
						Create Idea
					</EmptyStateButton>
				</EmptyState>
			) : (
				<PageColumn>
					<PageHeader
						title="Ideas"
						backTo={{ path: `/trips/${tripId}`, label: trip.name }}
						subtitle={countLabel(ideas.length, 'idea')}
						actions={
							<AddIdeaButton onClick={() => setAddingFrom('ideas_list')} data-testid={TestIds.common.fab}>
								<PlusIcon sx={{ fontSize: iconSizes.sm }} />
								Add Idea
							</AddIdeaButton>
						}
					/>

					<IdeaFilterRow
						ideas={ideas}
						category={selectedCategory}
						location={selectedLocation}
						onCategoryChange={setSelectedCategory}
						onLocationChange={setSelectedLocation}
					/>
					{visibleIdeas.length === 0 ? (
						<NoMatches>No ideas match these filters.</NoMatches>
					) : (
						<IdeaTileGrid
							ideas={visibleIdeas}
							onSelect={idea => navigate(`/trips/${tripId}/ideas/${idea.id}`)}
							showAttachments
						/>
					)}
				</PageColumn>
			)}

			<AddIdeaModal
				isOpen={addingFrom !== null}
				onClose={() => setAddingFrom(null)}
				onSubmit={handleCreateIdea}
				categoryFilter={selectedCategory ?? undefined}
			/>
		</PageScroll>
	)
}

export default IdeasPage
